import { patchConversation, talkToCoach } from 'api/v2/AI';
import { Button } from 'components/atoms/Button';
import { useAIChatContext } from 'components/organisms/AIChat/AIChat.context';
import getIntercom from 'hooks/intercom.hook';
import useConfirm from 'hooks/useConfirm';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import type {
  ConversationMessagePart,
  ConversationMessageTextPart,
} from 'types/ai.types';

const isTextPart = (
  part: ConversationMessagePart,
): part is ConversationMessageTextPart => part.type === 'text';

type AIMessageButtonTalkToCoachProps = {
  messageId: string;
};

const AIMessageButtonTalkToCoach: FC<AIMessageButtonTalkToCoachProps> = ({
  messageId,
}) => {
  const { conversationId, conversation } = useAIChatContext();
  const { t } = useTranslation();
  const { showNewMessage } = getIntercom();

  const { mutateAsync: talkToTaxCoach } = useMutation(() =>
    talkToCoach(conversationId, messageId),
  );

  const { mutateAsync: patchConversationIntercomShare } = useMutation(() =>
    patchConversation(conversationId, { intercomShareConversation: true }),
  );

  const [confirmContent, confirmAPI] = useConfirm({
    className: 'text-center',
    buttonsWrapperClassName: 'justify-center',
    okText: t('yes'),
    cancelText: t('no'),
  });

  const handleTalkToCoachConfirm = (forwardChat: boolean) => {
    const content = forwardChat
      ? conversation?.messages.reduce((acc, { role, parts }) => {
          const user = role === 'user' ? 'User' : 'AI Assistant';
          acc += `${user}: ${parts
            ?.filter(isTextPart)
            ?.map(({ content }) => content)
            .join('')}\n\n---\n\n`;
          return acc;
        }, '')
      : undefined;

    showNewMessage(t('ai_bot.talk_to_coach.message_history', { content }));
    talkToTaxCoach();
    confirmAPI.close();
  };

  const handleTalkToCoach = () =>
    confirmAPI.open({
      message: t('ai_bot.talk_to_tax_coach.share_conversation'),
      onConfirm: async () => {
        handleTalkToCoachConfirm(true);
        await patchConversationIntercomShare();
      },
      onDecline: () => handleTalkToCoachConfirm(false),
    });

  return (
    <>
      {confirmContent}

      <Button
        className="self-end mt-2"
        onClick={handleTalkToCoach}
        structure="text"
      >
        {t('talk_to_an_expert')}
      </Button>
    </>
  );
};

export default AIMessageButtonTalkToCoach;
